/**
 * Chat mentions
 *
 * Pure helpers for parsing and serializing structured chat mentions.
 *
 * Primary responsibilities:
 * - Parse canonical `@[Display](kind:id)` mention markup
 * - Normalize supported legacy mention markup
 * - Strip mention markup for prompt text without touching normal links
 *
 * Dependencies:
 * - None
 */

export const CHAT_MENTION_KINDS = [
  'losi',
  'everyone',
  'member',
  'assistant',
  'space_member',
  'crm',
  'space_item',
  'staffing_agent'
] as const

export type ChatMentionKind = (typeof CHAT_MENTION_KINDS)[number]

export interface ChatMention {
  kind: ChatMentionKind
  id: string
  display: string
}

export type ChatMentionInput = Pick<ChatMention, 'kind' | 'id' | 'display'>

export const PLATFORM_LOSI_MENTION = {
  kind: 'losi',
  id: 'platform',
  display: 'Losi'
} as const satisfies ChatMention

const CHAT_MENTION_KIND_SET: ReadonlySet<string> = new Set(CHAT_MENTION_KINDS)

const LEGACY_MENTION_KIND_ALIASES: Partial<Record<string, ChatMentionKind>> = {
  agent: 'assistant',
  company: 'crm',
  contact: 'crm',
  lead: 'crm'
}

const CANONICAL_MENTION_REGEX =
  /@\[([^\]]+)\]\(([^:()\s]+):([^)]+)\)/g
const LEGACY_PLATFORM_LOSI_MENTION_REGEX =
  /(^|[^@])\[([^\]]+)\]\((losi|platform[-_]losi|platform):(platform|losi|losi-assistant|losi-assistant-mention)\)/gi
const LEGACY_SPACE_MEMBER_MENTION_REGEX =
  /(^|[^@])\[([^\]]+)\]\((space[-_]member):([^)]+)\)/gi
const PLAIN_EVERYONE_MENTION_REGEX =
  /(^|[\s([{'"“‘])@everyone(?=$|[^\w-])/i
const QUOTE_BLOCK_REGEX = /\[QUOTE:[^\]]*\][\s\S]*?\[\/QUOTE\]\s*/gi
const NOTE_CONTEXT_REGEX =
  /^NOTE CONTEXT \(Title:[^\n]*\)\n"""\n[\s\S]*?"""\n\n?/i

type IndexedChatMention = ChatMention & {
  index: number
}

const normalizePart = (value: string | null | undefined) => {
  const normalized = value?.trim()

  return normalized ? normalized : null
}

const isChatMentionKind = (value: string): value is ChatMentionKind =>
  CHAT_MENTION_KIND_SET.has(value)

const normalizeMentionKind = (
  value: string | null | undefined
): ChatMentionKind | null => {
  const normalized = normalizePart(value)?.toLowerCase().replace(/-/g, '_')

  if (!normalized) {
    return null
  }

  if (isChatMentionKind(normalized)) {
    return normalized
  }

  return LEGACY_MENTION_KIND_ALIASES[normalized] ?? null
}

const isPlatformLosiMentionParts = (
  kind: string | null | undefined,
  id: string | null | undefined
) => {
  const normalizedKind = normalizePart(kind)?.toLowerCase().replace(/-/g, '_')
  const normalizedId = normalizePart(id)?.toLowerCase()

  if (!normalizedKind || !normalizedId) {
    return false
  }

  return (
    (normalizedKind === 'losi' &&
      ['platform', 'losi', 'losi-assistant', 'losi-assistant-mention'].includes(
        normalizedId
      )) ||
    ((normalizedKind === 'platform' || normalizedKind === 'platform_losi') &&
      ['losi', 'losi-assistant', 'losi-assistant-mention'].includes(
        normalizedId
      ))
  )
}

const normalizeLegacySpaceMemberDisplay = (value: string) =>
  normalizePart(value)?.replace(/^@+/, '').trim() || null

const getRequiredMentionPart = (value: string, field: string) => {
  const normalized = normalizePart(value)

  if (!normalized) {
    throw new Error(`Chat mention ${field} is required`)
  }

  return normalized
}

const appendCanonicalMentions = (
  mentions: IndexedChatMention[],
  content: string
) => {
  for (const match of content.matchAll(CANONICAL_MENTION_REGEX)) {
    const display = normalizePart(match[1])
    const rawKind = normalizePart(match[2])
    const kind = normalizeMentionKind(match[2])
    const id = normalizePart(match[3])
    const isPlatformLosi = isPlatformLosiMentionParts(rawKind, id)
    const normalizedKind = isPlatformLosi ? 'losi' : kind

    if (!display || !id || !normalizedKind) {
      continue
    }

    mentions.push({
      kind: normalizedKind,
      id: isPlatformLosi ? 'platform' : id,
      display,
      index: match.index ?? 0
    })
  }
}

const appendLegacySpaceMemberMentions = (
  mentions: IndexedChatMention[],
  content: string
) => {
  for (const match of content.matchAll(LEGACY_SPACE_MEMBER_MENTION_REGEX)) {
    const display = normalizeLegacySpaceMemberDisplay(match[2] || '')
    const kind = normalizeMentionKind(match[3])
    const id = normalizePart(match[4])

    if (!display || kind !== 'space_member' || !id) {
      continue
    }

    mentions.push({
      kind,
      id,
      display,
      index: (match.index ?? 0) + (match[1]?.length ?? 0)
    })
  }
}

const appendLegacyPlatformLosiMentions = (
  mentions: IndexedChatMention[],
  content: string
) => {
  for (const match of content.matchAll(LEGACY_PLATFORM_LOSI_MENTION_REGEX)) {
    const display = normalizePart(match[2])

    if (!display) {
      continue
    }

    mentions.push({
      kind: 'losi',
      id: 'platform',
      display,
      index: (match.index ?? 0) + (match[1]?.length ?? 0)
    })
  }
}

export const parseChatMentions = (content: string): ChatMention[] => {
  const mentions: IndexedChatMention[] = []

  appendCanonicalMentions(mentions, content)
  appendLegacyPlatformLosiMentions(mentions, content)
  appendLegacySpaceMemberMentions(mentions, content)

  return mentions
    .sort((first, second) => first.index - second.index)
    .map(({ index: _index, ...mention }) => mention)
}

export const hasPlatformLosiMention = (content: string) =>
  parseChatMentions(content).some(
    (mention) =>
      mention.kind === PLATFORM_LOSI_MENTION.kind &&
      mention.id.toLowerCase() === PLATFORM_LOSI_MENTION.id
  )

export const hasEveryoneMention = (content: string) =>
  PLAIN_EVERYONE_MENTION_REGEX.test(content) ||
  parseChatMentions(content).some((mention) => mention.kind === 'everyone')

export const getMentionedMemberUserIds = (content: string) => {
  const userIds: string[] = []
  const seen = new Set<string>()

  for (const mention of parseChatMentions(content)) {
    if (mention.kind !== 'member' && mention.kind !== 'space_member') {
      continue
    }

    if (seen.has(mention.id)) {
      continue
    }

    seen.add(mention.id)
    userIds.push(mention.id)
  }

  return userIds
}

export const getMentionedStaffingAgentIds = (content: string) => {
  const agentIds: string[] = []
  const seen = new Set<string>()

  for (const mention of parseChatMentions(content)) {
    if (mention.kind !== 'staffing_agent') {
      continue
    }

    if (seen.has(mention.id)) {
      continue
    }

    seen.add(mention.id)
    agentIds.push(mention.id)
  }

  return agentIds
}

const stripLeadingSelectionContext = (content: string) => {
  if (!content.startsWith('"')) {
    return content
  }

  const closingQuoteIndex = content.indexOf('"\n\n', 1)

  return closingQuoteIndex >= 0
    ? content.slice(closingQuoteIndex + 3).trimStart()
    : content
}

export const stripChatMentionNotificationContext = (content: string) =>
  stripLeadingSelectionContext(
    content
      .replace(QUOTE_BLOCK_REGEX, '')
      .replace(NOTE_CONTEXT_REGEX, '')
      .trimStart()
  )

export const serializeChatMention = (mention: ChatMentionInput) => {
  const display = getRequiredMentionPart(mention.display, 'display')
  const id = getRequiredMentionPart(mention.id, 'id')

  return `@[${display}](${mention.kind}:${id})`
}

export const stripMentionMarkupForPrompt = (content: string) =>
  content
    .replace(
      CANONICAL_MENTION_REGEX,
      (
        match: string,
        rawDisplay: string,
        rawKind: string,
        rawId: string
      ) => {
        const display = normalizePart(rawDisplay)
        const kind = normalizeMentionKind(rawKind)
        const id = normalizePart(rawId)

        return display && id && (kind || isPlatformLosiMentionParts(rawKind, id))
          ? display
          : match
      }
    )
    .replace(
      LEGACY_PLATFORM_LOSI_MENTION_REGEX,
      (
        match: string,
        prefix: string,
        rawDisplay: string
      ) => {
        const display = normalizePart(rawDisplay)

        return display ? `${prefix}${display}` : match
      }
    )
    .replace(
      LEGACY_SPACE_MEMBER_MENTION_REGEX,
      (
        match: string,
        prefix: string,
        rawDisplay: string,
        rawKind: string,
        rawId: string
      ) => {
        const display = normalizeLegacySpaceMemberDisplay(rawDisplay)
        const kind = normalizeMentionKind(rawKind)
        const id = normalizePart(rawId)

        return display && kind === 'space_member' && id
          ? `${prefix}${display}`
          : match
      }
    )
